import { Campaign, AdGroup, BrandListAssociation } from '@bingads-webui/campaign-odata-schemas';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { bulkModelToViewModelForBudget, viewModelToModelForBudget } from './budget';
import { viewModelToBulkModelForSchedule } from './campaign-criterion';
import { viewModelToModelForAdGroupBudget, bulkModelToViewModelForAdGroupBudget } from './ad-group-for-budget';
import { isBulkWrappedResponse } from '../shared/bulk';
import { ERROR_ENTITY, getBulkUploadErrorType, modelToViewModelForBulkErrors, removeReferencedEntityInvalidIdErrors } from '../shared/bulk-upsert-errors';
import { GENERIC_ERROR_MESSAGE_KEY, getErrorsPlaceholders } from '../shared/constants';

const campaignEDM = edmCampaign.types.resolve('Model.Campaign');
const campaignResource = new campaignEDM.ResourceIdentifier();

const createOrUpdateCampaign = (data) => {
  const createOrUpdateCall = data && data.Id ?
    campaignResource.bulkUpdate.bind(campaignResource) :
    campaignResource.bulkCreate.bind(campaignResource);
  return createOrUpdateCall(data);
};

const bulkUpsertEntityErrorMap = {
  [getBulkUploadErrorType(Campaign)]: ERROR_ENTITY.Campaign,
  [getBulkUploadErrorType(AdGroup)]: ERROR_ENTITY.AdGroup,
  'Campaign Day Time Criterion Error': ERROR_ENTITY.CampaignDayTimeCriterion,
  [getBulkUploadErrorType(BrandListAssociation)]: ERROR_ENTITY.BrandListAssociation,
};

export const saveBudget = ({
  campaign: campaignViewModel,
  adGroup: adGroupViewModel,
}, {
  scenario,
  i18n,
  activityLogger,
}) => {
  const campaignModel = viewModelToModelForBudget(campaignViewModel);
  const adgorupModel = viewModelToModelForAdGroupBudget(adGroupViewModel, campaignViewModel.Id, i18n);
  const scheduleModel = campaignViewModel.adSchedulesBulkUpsert ? viewModelToBulkModelForSchedule(campaignViewModel.adSchedulesBulkUpsert, campaignModel) : [];

  const modelToSave = {
    ...campaignModel,
    CampaignCriterions: [
      ...scheduleModel,
    ],
    AdGroups: [adgorupModel],
  };

  return createOrUpdateCampaign(modelToSave)
    // with bulk the result will have shape of:
    // - { ...entityData } - in case of success;
    // - { entity: { ...entityData }, errors: [(EditorialError|MTError)] } - in case of error(s).
    .then((result) => {
      const data = {
        campaign: bulkModelToViewModelForBudget({
          campaignModel: result,
          originalCampaignModel: campaignViewModel,
        }),
        adGroup: bulkModelToViewModelForAdGroupBudget({
          adGroupModel: result && result.AdGroups && result.AdGroups[0],
          i18n,
        }),
        ...getErrorsPlaceholders(),
      };

      return data;
    }, (bulkOrSystemError) => {
      if (isBulkWrappedResponse(bulkOrSystemError)) {
        const { entity, errors } = bulkOrSystemError;
        const campaign = entity ?
          bulkModelToViewModelForBudget({
            campaignModel: entity,
            originalCampaignModel: campaignViewModel,
          }) :
          campaignViewModel;

        let filteredErrors = errors;
        if (campaign.Id <= 0) {
          filteredErrors = removeReferencedEntityInvalidIdErrors(errors);
        }

        const serverErrors = modelToViewModelForBulkErrors(
          filteredErrors,
          bulkUpsertEntityErrorMap,
          scenario,
          i18n,
          activityLogger
        );

        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({
          campaign,
          adGroup: entity && entity.AdGroups ? bulkModelToViewModelForAdGroupBudget({
            adGroupModel: entity.AdGroups[0],
            i18n,
          }) : adGroupViewModel,
          serverErrors,
        });
      }
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        campaign: campaignViewModel,
        adGroup: adGroupViewModel,
        systemErrors: [i18n.getString(GENERIC_ERROR_MESSAGE_KEY)],
      });
    });
};
